import ModalLayer from './ModalLayer';
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { FaEye, FaEyeSlash, FaArrowLeft, FaSyncAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { BACKEND_URL } from '../constants';
import './SignUp.css';

const EyeIcon = FaEye as React.ElementType;
const EyeSlashIcon = FaEyeSlash as React.ElementType;

/* =========================
   Modal (simple)
========================= */
interface PopupModalProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
  onAction?: () => void;
  actionLabel?: string;
}
const PopupModal: React.FC<PopupModalProps> = ({ message, type, onClose, onAction, actionLabel }) => {
  return (
    <ModalLayer className="sign-modalOverlay" role="dialog" aria-modal="true">
      <div className="sign-modalCard">
        <p className={`sign-modalText ${type === 'success' ? 'ok' : 'err'}`}>{message}</p>
        <div className="sign-modalActions">
          {onAction ? (
            <button className="sign-modalBtn sign-modalBtnPrimary" onClick={onAction}>
              {actionLabel || (type === 'success' ? 'Sign in' : 'Go to Log In')}
            </button>
          ) : (
            <button className="sign-modalBtn" onClick={onClose}>OK</button>
          )}
        </div>
      </div>
    </ModalLayer>
  );
};

/* =========================
   Types
========================= */
interface Faculty {
  id: number;
  name: string;
  description: string;
}

interface Department {
  id: number;
  departmentCode: string;
  name: string;
  description: string;
  facultyId: number;
}

interface SignUpFormData {
  title: string;
  name: string;
  surname: string;
  specialization: string;
  gender: string;
  nationalId: string;
  dateOfBirth: string;
  nationality: string;
  highestEducation: string;
  academicQualifications: string;
  contactNumber: string;
  registrationNo: string;
  email: string;
  password: string;
  confirmPassword: string;
  facultyId: string;   // faculty id as string
  departmentId: string; // department id as string
}

interface PopupState {
  visible: boolean;
  message: string;
  type: 'success' | 'error';
  onAction?: () => void;
  actionLabel?: string;
}

/* =========================
   Helpers
========================= */
const generateStrongPassword = (length = 12): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()-_=+[]{}<>?';

  const all = upper + lower + numbers + symbols;

  // Ensure all categories are represented
  let password =
    upper[Math.floor(Math.random() * upper.length)] +
    lower[Math.floor(Math.random() * lower.length)] +
    numbers[Math.floor(Math.random() * numbers.length)] +
    symbols[Math.floor(Math.random() * symbols.length)];

  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  // Simple shuffle
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
};

const SignUp: React.FC = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<SignUpFormData>({
    title: '',
    name: '',
    surname: '',
    specialization: '',
    gender: '',
    nationalId: '',
    dateOfBirth: '',
    nationality: '',
    highestEducation: '',
    academicQualifications: '',
    contactNumber: '',
    registrationNo: '',
    email: '',
    password: '',
    confirmPassword: '',
    facultyId: '',
    departmentId: ''
  });
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [popup, setPopup] = useState<PopupState>({
    visible: false,
    message: '',
    type: 'error'
  });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  /* =========================
     Password rules (live)
  ========================= */
  const passwordCriteria = {
    length: formData.password.length >= 8,
    upper: /[A-Z]/.test(formData.password),
    lower: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    special: /[^A-Za-z0-9]/.test(formData.password),
  };

  const criteriaCount = Object.values(passwordCriteria).filter(Boolean).length;
  const passwordIsValid = criteriaCount === 5;
  const passwordStrengthPercent = (criteriaCount / 5) * 100;
  const passwordStrengthLabel =
    criteriaCount === 0 ? 'Too short' :
    criteriaCount <= 2 ? 'Weak' :
    criteriaCount <= 4 ? 'Good' :
    'Strong';

  /* =========================
     Fetch faculties & departments
  ========================= */
  useEffect(() => {
    const fetchFaculties = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/v1/api/systems-settings/faculties/get-all`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setFaculties(data);
        }
      } catch (error) {
        console.error('Error fetching faculties:', error);
      }
    };
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/v1/api/departments/all`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setDepartments(data);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchFaculties();
    fetchDepartments();
  }, []);

  // Filter departments by selected faculty
  const filteredDepartments = formData.facultyId
    ? departments.filter(dep => dep.facultyId === Number(formData.facultyId))
    : [];

  // If faculty changes, clear departmentId
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      departmentId: ''
    }));
    // eslint-disable-next-line
  }, [formData.facultyId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Step 1 validation & next
  const handleNext = (e: FormEvent) => {
    e.preventDefault();
    if (
      !formData.title ||
      !formData.name ||
      !formData.surname ||
      !formData.specialization ||
      !formData.gender ||
      !formData.nationalId ||
      !formData.dateOfBirth ||
      !formData.nationality ||
      !formData.highestEducation ||
      !formData.academicQualifications ||
      !formData.contactNumber ||
      !formData.facultyId ||
      !formData.departmentId
    ) {
      setPopup({ visible: true, message: 'Please fill in all the required fields.', type: 'error' });
      return;
    }

    // Check if user is at least 18 years old
    const dob = new Date(formData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age < 18) {
      setPopup({ visible: true, message: 'You must be at least 18 years old.', type: 'error' });
      return;
    }

    setStep(2);
  };

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword(14);
    setFormData(prev => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword,
    }));
    setPasswordTouched(true);
    setConfirmPasswordTouched(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setPopup({ visible: true, message: 'Please fill in all the required fields.', type: 'error' });
      return;
    }

    if (!passwordIsValid) {
      setPopup({
        visible: true,
        message:
          'Password is too weak. It must be at least 8 characters and include uppercase, lowercase, a number, and a special character.',
        type: 'error',
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setPopup({ visible: true, message: 'Passwords do not match.', type: 'error' });
      return;
    }

    // departmentId as number, from department select
    const payload: any = {
      name: formData.name,
      surname: formData.surname,
      title: formData.title,
      specialization: formData.specialization,
      gender: formData.gender,
      nationalId: formData.nationalId,
      dateOfBirth: formData.dateOfBirth,
      nationality: formData.nationality,
      highestLevelOfEducation: formData.highestEducation,
      academicQualifications: formData.academicQualifications,
      contactNumber: formData.contactNumber,
      email: formData.email,
      password: formData.password,
      departmentId: Number(formData.departmentId),
    };

    if (formData.registrationNo && formData.registrationNo.trim() !== '') {
      payload.registrationNo = formData.registrationNo.trim();
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(`${BACKEND_URL}/v1/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setPopup({
          visible: true,
          message: 'Registration successful!',
          type: 'success',
          onAction: () => {
            setPopup({ visible: false, message: '', type: 'error' });
            navigate('/login');
          },
          actionLabel: 'Sign in'
        });
      } else if (response.status === 409) {
        setPopup({
          visible: true,
          message: 'Account already exists. Please log in.',
          type: 'error',
          onAction: () => {
            setPopup({ visible: false, message: '', type: 'error' });
            navigate('/login');
          },
          actionLabel: 'Go to Log In'
        });
      } else {
        setPopup({ visible: true, message: 'Registration failed. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error during registration:', error);
      setPopup({ visible: true, message: 'An error occurred. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate max date for Date of Birth (must be at least 18 years ago)
  const today = new Date();
  const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  const maxDate = eighteenYearsAgo.toISOString().split('T')[0];

  const canSubmit =
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    passwordIsValid &&
    formData.password === formData.confirmPassword &&
    !isSubmitting;

  return (
    <div className="sign-page">
      <main className="sign-center">
        <div className="sign-shell">
          {/* Left side / branding panel (modern layout hook for CSS) */}
          <section className="sign-heroPanel">
            <div className="sign-heroContent">
              <h1 className="sign-heroTitle">Create your account</h1>
              <p className="sign-heroSubtitle">
                Join the platform, manage your research profile, and track your applications in one place.
              </p>
              <div className="sign-steps">
                <div className={`sign-step ${step === 1 ? 'active' : ''}`}>
                  <span className="sign-stepNumber">1</span>
                  <span className="sign-stepLabel">Personal Details</span>
                </div>
                <div className={`sign-step ${step === 2 ? 'active' : ''}`}>
                  <span className="sign-stepNumber">2</span>
                  <span className="sign-stepLabel">Account Security</span>
                </div>
              </div>
            </div>
          </section>

          {/* Right side / form card */}
          <section className="sign-container">
            <div className="sign-pageIntro">
              <div>
                <span className="sign-kicker">Research Ethics Portal</span>
                <h1>Create your account</h1>
                <p>Complete your profile manually so your faculty, department, and application routing are captured correctly.</p>
              </div>
              <div className="sign-steps" aria-label="Signup progress">
                <div className={`sign-step ${step === 1 ? 'active' : ''}`}>
                  <span className="sign-stepNumber">1</span>
                  <span className="sign-stepLabel">Profile</span>
                </div>
                <div className={`sign-step ${step === 2 ? 'active' : ''}`}>
                  <span className="sign-stepNumber">2</span>
                  <span className="sign-stepLabel">Security</span>
                </div>
              </div>
            </div>

            {step === 1 && (
              <form className="sign-form" onSubmit={handleNext}>
                <h2 className="sign-title">Step 1 · Personal Information</h2>
                <p className="sign-subtitle">Tell us about yourself to set up your profile.</p>
                <hr className="sign-cardSeparator" />

                <div className="sign-field">
                  <label className="sign-label" htmlFor="registrationNo">
                    EC / Registration Number{' '}
                    <span className="sign-labelHint">(if applicable)</span>
                  </label>
                  <input
                    className="sign-input"
                    type="text"
                    id="registrationNo"
                    name="registrationNo"
                    value={formData.registrationNo}
                    onChange={handleChange}
                    placeholder="Optional"
                    autoComplete="off"
                  />
                </div>

                <div className="sign-fieldRow">
                  <div className="sign-field">
                    <label className="sign-label" htmlFor="title">Title</label>
                    <select
                      className="sign-input"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    >
                      <option value="">--Select Title--</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Prof.">Prof.</option>
                      <option value="Rev.">Rev.</option>
                      <option value="Hon.">Hon.</option>
                    </select>
                  </div>

                  <div className="sign-field">
                    <label className="sign-label" htmlFor="name">First Name(s)</label>
                    <input
                      className="sign-input"
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="surname">Surname</label>
                  <input
                    className="sign-input"
                    type="text"
                    id="surname"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="specialization">Specialization</label>
                  <input
                    className="sign-input"
                    type="text"
                    id="specialization"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="sign-fieldRow">
                  <div className="sign-field">
                    <label className="sign-label" htmlFor="gender">Gender</label>
                    <select
                      className="sign-input"
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                    >
                      <option value="">--Select--</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="nonbinary">Non-Binary</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="sign-field">
                    <label className="sign-label" htmlFor="nationalId">National ID</label>
                    <input
                      className="sign-input"
                      type="text"
                      id="nationalId"
                      name="nationalId"
                      value={formData.nationalId}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="sign-fieldRow">
                  <div className="sign-field">
                    <label className="sign-label" htmlFor="dateOfBirth">Date of Birth</label>
                    <input
                      className="sign-input"
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                      max={maxDate}
                    />
                  </div>
                  <div className="sign-field">
                    <label className="sign-label" htmlFor="nationality">Nationality</label>
                    <input
                      className="sign-input"
                      type="text"
                      id="nationality"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="highestEducation">Highest Level of Education</label>
                  <input
                    className="sign-input"
                    type="text"
                    id="highestEducation"
                    name="highestEducation"
                    value={formData.highestEducation}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="academicQualifications">Academic Qualifications</label>
                  <input
                    className="sign-input"
                    type="text"
                    id="academicQualifications"
                    name="academicQualifications"
                    placeholder="B.Sc, M.Sc, PhD"
                    value={formData.academicQualifications}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="contactNumber">Contact Number</label>
                  <input
                    className="sign-input"
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    placeholder="+263"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="facultyId">Select Faculty</label>
                  <select
                    className="sign-input"
                    id="facultyId"
                    name="facultyId"
                    value={formData.facultyId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">--Select Faculty--</option>
                    {faculties.map(faculty => (
                      <option key={faculty.id} value={faculty.id}>{faculty.name}</option>
                    ))}
                  </select>
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="departmentId">Select Department</label>
                  <select
                    className="sign-input"
                    id="departmentId"
                    name="departmentId"
                    value={formData.departmentId}
                    onChange={handleChange}
                    required
                    disabled={!formData.facultyId}
                  >
                    <option value="">--Select Department--</option>
                    {filteredDepartments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                <button type="submit" className="sign-nextBtn">Continue to Account</button>
                <p className="sign-loginPrompt">
                  Already have an account? <Link to="/login">Log in</Link>
                </p>
              </form>
            )}

            {step === 2 && (
              <form className="sign-form" onSubmit={handleSubmit}>
                <div className="sign-formHeader">
                  <button
                    type="button"
                    className="sign-back"
                    onClick={() => setStep(1)}
                  >
                    <FaArrowLeft className="sign-backIcon" />
                    <span>Back</span>
                  </button>
                </div>

                <h2 className="sign-title">Step 2 · Account Security</h2>
                <p className="sign-subtitle">
                  Secure your account with a strong password.
                </p>
                <hr className="sign-cardSeparator" />

                <div className="sign-field">
                  <label className="sign-label" htmlFor="email">Email Address</label>
                  <input
                    className="sign-input"
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="password">Password</label>
                  <div className="sign-passwordRow">
                    <div className="sign-passwordWrapper">
                      <input
                        className="sign-input"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        onBlur={() => setPasswordTouched(true)}
                        required
                      />
                      <button
                        type="button"
                        className="sign-passwordToggle"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                      </button>
                    </div>
                    <button
                      type="button"
                      className="sign-generateBtn"
                      onClick={handleGeneratePassword}
                    >
                      <FaSyncAlt className="sign-generateIcon" />
                      Generate
                    </button>
                  </div>

                  {/* Strength bar */}
                  <div className="sign-passwordStrengthBar">
                    <div
                      className={`sign-passwordStrengthFill sign-passwordStrengthFill--${
                        criteriaCount === 0 ? 'empty' :
                        criteriaCount <= 2 ? 'weak' :
                        criteriaCount <= 4 ? 'medium' : 'strong'
                      }`}
                      style={{ width: `${passwordStrengthPercent}%` }}
                    />
                  </div>
                  <div className="sign-passwordStrengthLabel">
                    {passwordStrengthLabel}
                  </div>

                  {/* Criteria checklist */}
                  <ul className="sign-passwordCriteria">
                    <li className={passwordCriteria.length ? 'ok' : ''}>
                      {passwordCriteria.length ? <FaCheckCircle /> : <FaTimesCircle />}
                      <span>At least 8 characters</span>
                    </li>
                    <li className={passwordCriteria.upper ? 'ok' : ''}>
                      {passwordCriteria.upper ? <FaCheckCircle /> : <FaTimesCircle />}
                      <span>One uppercase letter (A–Z)</span>
                    </li>
                    <li className={passwordCriteria.lower ? 'ok' : ''}>
                      {passwordCriteria.lower ? <FaCheckCircle /> : <FaTimesCircle />}
                      <span>One lowercase letter (a–z)</span>
                    </li>
                    <li className={passwordCriteria.number ? 'ok' : ''}>
                      {passwordCriteria.number ? <FaCheckCircle /> : <FaTimesCircle />}
                      <span>One number (0–9)</span>
                    </li>
                    <li className={passwordCriteria.special ? 'ok' : ''}>
                      {passwordCriteria.special ? <FaCheckCircle /> : <FaTimesCircle />}
                      <span>One special character (!, @, #, etc.)</span>
                    </li>
                  </ul>

                  {passwordTouched && !passwordIsValid && (
                    <p className="sign-errorText">
                      Please meet all the password requirements above.
                    </p>
                  )}
                </div>

                <div className="sign-field">
                  <label className="sign-label" htmlFor="confirmPassword">Confirm Password</label>
                  <div className="sign-passwordWrapper">
                    <input
                      className="sign-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={() => setConfirmPasswordTouched(true)}
                      required
                    />
                    <button
                      type="button"
                      className="sign-passwordToggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {confirmPasswordTouched &&
                    formData.confirmPassword &&
                    formData.password !== formData.confirmPassword && (
                      <p className="sign-errorText">Passwords do not match.</p>
                    )}
                </div>

                <button
                  type="submit"
                  className="sign-submitBtn"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? 'Creating account…' : 'Create account'}
                </button>

                <p className="sign-loginPrompt">
                  Already have an account? <Link to="/login">Log in</Link>
                </p>
              </form>
            )}

            {popup.visible && (
              <PopupModal
                message={popup.message}
                type={popup.type}
                onClose={() =>
                  setPopup({ visible: false, message: '', type: 'error' })
                }
                onAction={popup.onAction}
                actionLabel={popup.actionLabel}
              />
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SignUp;
